import {
  cannectDefaultProduct,
  cannectDefaultProductCapsule,
  cannectDefaultProductCream,
  cannectDefaultProductGummy,
  cannectDefaultProductNoTarg,
  cannectDefaultProductOil
} from '@cannect/assets/img'
import { useProductClassId } from '@cannect/services/resources/classes'
import { useAuth } from '@cannect/hooks/AuthContext'
import { useFlavorManager } from '@cannect/hooks/useFlavorManager'
import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { useSuggestedProducts } from '@cannect/hooks/useSuggestedProducts'
import { ShoppingCartIcon } from 'lucide-react'
import React, { useState } from 'react'
import FlavorSelect from '../../../../../new-components/FlavorSelect/FlavorSelect'

interface Flavor {
  id: number
  mainProduct: number
  codigo: number
  flavor: string
  balance: number
  reserved: number
  total: number
}

interface RelatedProduct {
  id: number
  name?: string
  translated_name?: string
  flavor?: any
  flavor_id?: number
  original_price?: number
  current_price?: number
  price: number
  promotional_price?: number
  media?: Array<{ path: string; format: string }>
  currency?: string
  real_price?: number
  quantity?: number
  mainProduct?: number
  main_product_id?: any
  brand_id?: number
  class_id?: number
  supplier_id?: number
  stock?: number
  promotion?: Array<{
    regular_price?: any
    promotional_price?: any
    discount_value?: any
    discount_type?: any
  }>
}

interface RelatedProductsProps {
  onAddToCart: (product: RelatedProduct, quantity: number) => void
}

export const RelatedProducts: React.FC<RelatedProductsProps> = ({ onAddToCart }) => {
  const { addToCart, lastOrderCreated, isUpdate, cartLoading } = usePrepareCart()

  const cartProductIds = (lastOrderCreated?.items || []).map((item: any) => item.product.id)

  const { getClassIdByName } = useProductClassId()
  const hygieneBeautyId = getClassIdByName('Higiene e Beleza')

  const {
    products,
    isLoading,
    error,
    addingProduct,
    handleAddToCart: handleAddSuggested
  } = useSuggestedProducts({
    limit: 3,
    excludeProductIds: cartProductIds,
    filterByCartItems: true,
    classId: hygieneBeautyId
  })

  const { handleFlavorChange } = useFlavorManager()

  const [productQuantities, setProductQuantities] = useState<Record<number, number>>({})
  const [selectedFlavors, setSelectedFlavors] = useState<Record<number, number>>({})
  const [openDropdownProductId, setOpenDropdownProductId] = useState<number | null>(null)

  const { role } = useAuth()

  if (isLoading || error || products.length === 0) {
    return null
  }

  const handleAddToCart = async (product: any) => {
    const qnt = productQuantities[product.id] || 1
    const selectedFlavorId = selectedFlavors[product.id]

    await handleAddSuggested(product, async (prod: any) => {
      if (selectedFlavorId && selectedFlavorId !== product.flavor_id) {
        await handleFlavorChange(selectedFlavorId, selectedFlavorId, qnt)
      } else {
        await addToCart(prod.id, qnt, prod.class_id, prod.brand_id, prod?.type)
      }
    })
  }

  const handleIncrement = (id: number) => setProductQuantities((prev) => ({ ...prev, [id]: (prev[id] || 1) + 1 }))

  const handleDecrement = (id: number) =>
    setProductQuantities((prev) => ({ ...prev, [id]: Math.max(1, (prev[id] || 1) - 1) }))

  const getProductImage = (product: any) => {
    const isNationalProduct = product?.supplier?.id === 18
    const defaultImage = isNationalProduct ? cannectDefaultProduct : cannectDefaultProductNoTarg
    const imgMedia = product.media.find((m: any) => m.format === 'IMG')?.path
    const shapeNameFormatted = product?.shape_id
    const isSupplementProduct = product?.class_id === 6
    const isPillProduct = product?.origin === 'pill'
    const isPatientUserType = role === 1

    let imageSource = ''

    if (isPillProduct) {
      return imgMedia
    }

    switch (shapeNameFormatted) {
      case 1:
        imageSource =
          isPatientUserType && !isSupplementProduct ? cannectDefaultProductOil : imgMedia || cannectDefaultProductOil
        break
      case 2:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductCapsule
            : imgMedia || cannectDefaultProductCapsule
        break
      case 4:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductCream
            : imgMedia || cannectDefaultProductCream
        break
      case 6:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductGummy
            : imgMedia || cannectDefaultProductGummy
        break
      default:
        imageSource = isPatientUserType && !isSupplementProduct ? defaultImage : imgMedia || defaultImage
        break
    }

    return imageSource
  }

  const handleSelectFlavor = (productId: number, flavorId: number) => {
    setSelectedFlavors((prev) => ({ ...prev, [productId]: flavorId }))
    setOpenDropdownProductId(null)
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div>
        {products.map((product: any, index: number) => (
          <div className="px-4 sm:px-6" key={product.id}>
            <div className="flex flex-col gap-4 overflow-visible py-4 sm:flex-row sm:items-center sm:justify-between sm:py-6">
              <div className="flex flex-col gap-3 overflow-visible sm:gap-6">
                {index === 0 && <h3 className="text-sm font-normal text-gray-900 sm:text-base">Aproveite também</h3>}

                <div className="flex items-start gap-3 sm:gap-2">
                  <img
                    src={getProductImage(product)}
                    alt={product.translated_name}
                    className="h-16 w-16 rounded-md border border-gray-200 object-cover sm:h-20 sm:w-20"
                  />

                  <div className="flex flex-1 flex-col gap-2 overflow-visible sm:flex-initial sm:gap-3">
                    <p className="text-sm font-semibold text-gray-900">{product.translated_name}</p>
                    <div className="flex items-center">
                      <div className="flex items-center overflow-visible rounded-full border border-gray-200">
                        <FlavorSelect
                          productId={product.id}
                          mainProductId={product?.main_product_id}
                          value={selectedFlavors[product.id] || product.flavor_id}
                          currentFlavor={product.flavor}
                          open={openDropdownProductId === product.id}
                          onOpen={() => setOpenDropdownProductId(product.id)}
                          onClose={() => setOpenDropdownProductId(null)}
                          onChange={(flavorId: number) => handleSelectFlavor(product.id, flavorId)}
                          disabled={cartLoading || addingProduct === product.id || isUpdate}
                          autoFetch
                          preloadOnMount
                          showButtonWhenNoFlavors={false}
                          minFlavorsToShowButton={2}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex w-full flex-row items-center justify-between gap-3 sm:min-w-[200px] sm:flex-col sm:items-end sm:gap-1">
                <div className="flex flex-col gap-2 sm:order-3 sm:gap-1">
                  <div className="flex items-center gap-2 sm:gap-3">
                    {product.promotion && product.promotion.promotional_price && (
                      <>
                        <span className="text-[10px] text-gray-400 line-through sm:text-xs">
                          R$ {parseFloat(product?.promotion?.regular_price?.toString() || '0')}
                        </span>
                        {product.promotion && product.promotion?.discount_type === 'percent' ? (
                          <span className="text-[10px] italic text-primary-500 line-through sm:text-[12px]">
                            -{product?.promotion?.discount_value || 0}%
                          </span>
                        ) : (
                          <span className="text-[10px] italic text-primary-500 line-through sm:text-[12px]">
                            - R$ {product.promotion?.discount_value}
                          </span>
                        )}
                      </>
                    )}
                    <span className="text-sm font-semibold text-gray-900 sm:text-base">
                      R${' '}
                      {(parseFloat(product?.promotion?.promotional_price) || product.real_price)
                        ?.toFixed(2)
                        .replace('.', ',')}
                    </span>
                  </div>

                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={cartLoading || addingProduct === product.id || isUpdate}
                    className="flex h-8 w-32 items-center justify-center gap-1 rounded-lg border-[1.5px] border-solid border-gray-200 bg-white text-xs font-medium text-black hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:h-7 sm:w-48">
                    {cartLoading || addingProduct === product.id ? (
                      <>
                        <div className="h-3 w-3 animate-spin border-2 border-gray-300 border-t-primary-600" />
                        <span>{cartLoading ? 'Processando...' : 'Adicionando...'}</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCartIcon className="h-3 w-3" />
                        <span>Adicionar</span>
                      </>
                    )}
                  </button>
                </div>

                <div className="flex items-center overflow-hidden rounded-md border-[0.5px] border-solid border-gray-100 sm:order-2">
                  <button
                    onClick={() => handleDecrement(product.id)}
                    disabled={cartLoading || addingProduct === product.id || isUpdate}
                    className="flex h-6 w-6 items-center justify-center border-none bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                    -
                  </button>
                  <span className="flex h-6 w-8 items-center justify-center border-x border-gray-300 bg-white text-sm font-medium sm:h-7 sm:w-9">
                    {productQuantities[product.id] || 1}
                  </span>
                  <button
                    onClick={() => handleIncrement(product.id)}
                    disabled={cartLoading || addingProduct === product.id || isUpdate}
                    className="flex h-6 w-6 items-center justify-center border-none bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                    +
                  </button>
                </div>
              </div>
            </div>

            {index < products.length - 1 && <hr className="h-[0.3px] border-none bg-neutral-100" />}
          </div>
        ))}
      </div>
    </div>
  )
}
